import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import pick from 'lodash/pick';
import {addSubtractDayIntervalEndDateExclusive} from '@store/coaching-plans';
import ManagerCoachingEffectivenessWidget from 'components/dashboard/coaching-effectiveness/ManagerCoachingEffectivenessWidget';
import UserStore from 'stores/UserStore';
import ManagerCoachingEffectivenessStore from 'stores/dashboard/ManagerCoachingEffectivenessStore';
import ManagerCoachingEffectivenessActions from 'actions/dashboard/ManagerCoachingEffectivenessActions';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import AuthStore from 'stores/AuthStore';
import Loading from 'components/pure/utils/Loading';
import fetchMissingUsers from 'utils/fetchMissingUsers';
import checkFields from 'utils/checkFields';
import {getManagerIdIfNotAuthUser} from 'components/StatsPage2.utils';

const widgetId = 'manager_coaching_effectiveness'; // unused in the component, but required by the API.
const widgetType = 'MANAGER_COACHING_EFFECTIVENESS';

export class ManagerCoachingEffectivenessPage extends React.PureComponent {
  render() {
    return <ManagerCoachingEffectiveness {...this.props} hasRenderedDashv2Gif />;
  }
}

export class CoachCoachingEffectivenessPage extends React.PureComponent {
  render() {
    return (
      <ManagerCoachingEffectiveness
        {...this.props}
        dataForCoach
        viewForCoach
        hasRenderedDashv2Gif
      />
    );
  }
}

@connectToStores
export default class ManagerCoachingEffectiveness extends React.PureComponent {
  static getStores() {
    return [ManagerCoachingEffectivenessStore, AuthStore, UserStore, CoachingPlanStore];
  }

  static getPropsFromStores() {
    const authUser = AuthStore.getState().user;
    return {
      ...pick(ManagerCoachingEffectivenessStore.getState(), ['widget']),
      ...pick(UserStore.getState(), ['users', 'userListLoading']),
      ...pick(CoachingPlanStore.getState(), [
        'coachingPlansForManager',
        'areCoachingPlansForManagerLoading',
        'wasErrorLoadingCoachingPlansForManager',
      ]),
      authUser: authUser,
    };
  }

  constructor(args) {
    super(args);
    this.state = {
      ready: false,
    };
  }

  componentDidMount() {
    // Avoid double-fetching widget data. componentDidUpdate will be called after initial mount with the selected team
    // which can lead to double-loading. The prop can already be set when navigating around in the menu, though, so we
    // need to fetch here, as there's no update on initial render.
    if (this.props.selectedTeam || this.props.dataForCoach) {
      this.getWidget();
    }
    this.getCoachingPlans();
  }

  componentDidUpdate(prevProps) {
    const {startDate: prevStartDate, endDate: prevEndDate} = prevProps;
    const {startDate, endDate} = this.props;
    const dateChanged = startDate !== prevStartDate || endDate !== prevEndDate;
    if (dateChanged) {
      this.getCoachingPlans();
    }
    if (
      checkFields(this.props, prevProps, [
        'startDate',
        'endDate',
        'selectedTeam',
        'coach',
        'manager',
      ])
    ) {
      this.getWidget();
    }
  }

  getWidget() {
    const {
      authUser,
      startDate,
      endDate,
      selectedTeam,
      // XXX Need doc on these props
      dataForCoach = false,
    } = this.props;
    const manager = dataForCoach ? this.props.coach : this.props.manager;
    const teamId = dataForCoach ? null : selectedTeam && selectedTeam.id;
    if (!manager) return;
    // maxDate isn't needed when subtracting.
    const {startDate: previousStartDate, endDate: previousEndDate} =
      addSubtractDayIntervalEndDateExclusive('subtract', startDate, endDate);
    const isSelf = manager.id === authUser.id;
    const label = dataForCoach
      ? 'Coaching Effectiveness'
      : `Coaching Received By ${isSelf ? 'Your' : manager.firstName + "'s"} Team`;
    const widgetConfig = {
      startDate,
      endDate,
      previousStartDate,
      previousEndDate,
      teamId,
      forCoach: dataForCoach,
      widgetId,
      widgetType,
      label,
    };

    this.setState({ready: false});
    setTimeout(() => {
      const managerId = getManagerIdIfNotAuthUser(this.props);
      ManagerCoachingEffectivenessActions.getCoachingEffectivenessReport(
        widgetConfig,
        managerId
      ).then(widget => this.fetchMissingUsers(widget));
    }, 0);
  }

  fetchMissingUsers(widget) {
    const report = widget.get('report');

    // Get ids of users reported on, plus any coaches.
    const reportUserIds = report
      .entrySeq()
      .map(([recipientUserId, recipientReport]) => {
        const coachUserIds = recipientReport.totalTimeReceived.keySeq().toSet();
        return coachUserIds.add(recipientUserId);
      })
      .flatten();

    // Either fire an action (if we need to get some users) and set ready state, or just set ready state (if we've
    // already got all the users we need).
    //
    // At this point the store loading flags will be reliable indicators of whether or not we have the user and widget
    // data for rendering.
    fetchMissingUsers(reportUserIds, false).then(() => this.setState({ready: true}));
  }

  getCoachingPlans() {
    const {startDate, endDate} = this.props;
    if (!startDate || !endDate) return;
    const start = startDate.format();
    // Endpoint is inclusive, but we want exclusivity.
    const end = endDate.clone().subtract(1, 'second').format();
    CoachingPlanActions.fetchForManagerWithReviewsInRange.defer(start, end);
  }

  render() {
    const {
      widget,
      viewForCoach,
      coachId,
      managerId,
      adminId,
      users,
      widgetLoading,
      userListLoading,
      coachingPlansForManager: coachingPlans,
      areCoachingPlansForManagerLoading: areCoachingPlansLoading,
      rows,
      showMoreLink,
      startDate,
      endDate,
      manager,
      fromPath,
      location,
      hasRenderedDashv2Gif = true,
    } = this.props;

    const {ready} = this.state;

    if (!hasRenderedDashv2Gif) return null;

    // TODO error handling.
    const isLoading = !ready || widgetLoading || userListLoading || areCoachingPlansLoading;
    return (
      <div className="standalone-widget-wrapper">
        {isLoading ? (
          <Loading />
        ) : (
          <ManagerCoachingEffectivenessWidget
            rows={rows}
            viewForCoach={viewForCoach}
            coachId={coachId}
            managerId={managerId}
            adminId={adminId}
            users={users}
            coachingPlans={coachingPlans}
            widget={widget}
            showMoreLink={showMoreLink}
            startDate={startDate}
            endDate={endDate}
            manager={manager}
            fromPath={fromPath}
            location={location}
          />
        )}
      </div>
    );
  }
}
